import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, CATEGORIES } from '@/lib/api';
import ProductCard from '@/components/product/ProductCard';
import ProductDetailModal from '@/components/product/ProductDetailModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { Filter, X, Info } from 'lucide-react';

// Sub-filters derived dynamically from products data in each main category
const SUBFILTERS = {};

const QUALITY_INFO = [
    { key: 'eco', label: 'Eco', desc: 'Prelam HDHMR (White) + EBCO Fittings', color: 'bg-slate-100 text-slate-800 border-slate-300' },
    { key: 'prime', label: 'Prime', desc: 'Prelam HDHMR (Brown Fabric) + EBCO Fittings', color: 'bg-blue-50 text-blue-900 border-blue-300' },
    { key: 'gold', label: 'Gold', desc: 'Prelam HDHMR (Brown Fabric) + Hettich Fittings', color: 'bg-amber-50 text-amber-900 border-amber-300' },
];

export default function Modules() {
    const [params, setParams] = useSearchParams();
    const initialCat = params.get('category') || null;
    const initialSub = params.get('subcategory') || null;
    const [products, setProducts] = useState([]);
    const [activeCat, setActiveCat] = useState(initialCat);
    const [activeSub, setActiveSub] = useState(initialSub);
    const [modalProduct, setModalProduct] = useState(null);
    const { globalQuality, setGlobalQuality } = useCart();

    useEffect(() => {
        api.get('/products', { params: { limit: 500 } }).then((r) => setProducts(r.data));
    }, []);

    useEffect(() => {
        const p = {};
        if (activeCat) p.category = activeCat;
        if (activeSub) p.subcategory = activeSub;
        setParams(p, { replace: true });
    }, [activeCat, activeSub, setParams]);

    const filtered = useMemo(() => {
        return products.filter((p) => {
            if (activeCat && p.category !== activeCat) return false;
            if (activeSub && (p.subcategory || '') !== activeSub) return false;
            return true;
        });
    }, [products, activeCat, activeSub]);

    // Sub-filters dynamically built from actual data of the active category
    const availableSubs = useMemo(() => {
        if (!activeCat) return [];
        const source = products.filter((p) => p.category === activeCat);
        const counts = new Map();
        for (const p of source) {
            const sub = (p.subcategory || '').trim();
            if (!sub) continue;
            counts.set(sub, (counts.get(sub) || 0) + 1);
        }
        return Array.from(counts.entries())
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count);
    }, [products, activeCat]);

    return (
        <div>
            <section className="bg-white border-b border-slate-200">
                <div className="kanbr-container py-8 md:py-12">
                    <div className="eyebrow text-slate-500 mb-2">Offering 03</div>
                    <h1 className="font-display text-2xl md:text-3xl font-bold text-amber-600 leading-tight tracking-tight">Pre-Crafted Modules (DIY)</h1>
                    <p className="text-slate-600 mt-3 text-sm md:text-base max-w-2xl leading-relaxed">Prefabricated kitchen cabinet boxes, wardrobes, dressers and TV units. Switch between Eco, Prime and Gold quality — prices update automatically.</p>

                    {/* Quality Tier definition */}
                    <div className="mt-6 border border-slate-200 bg-slate-50 p-4 md:p-5" data-testid="quality-info">
                        <div className="flex items-center gap-2 mb-3">
                            <Info className="w-4 h-4 text-blue-900" />
                            <div className="eyebrow">Quality tiers</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {QUALITY_INFO.map((q) => (
                                <div key={q.key} className={`border ${q.color} p-3`}>
                                    <div className="font-display font-bold text-base">{q.label}</div>
                                    <div className="text-xs mt-1 leading-relaxed">{q.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="kanbr-container py-8">
                <div className="border border-slate-200 bg-white p-4 md:p-5 mb-6 flex flex-wrap items-center gap-4" data-testid="modules-toolbar">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-slate-500" />
                        <div className="eyebrow">Global Quality Tier</div>
                    </div>
                    <Select value={globalQuality} onValueChange={setGlobalQuality}>
                        <SelectTrigger className="w-56 rounded-none h-10" data-testid="global-quality-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="eco">Eco — HDHMR Prelam</SelectItem>
                            <SelectItem value="prime">Prime — HDHMR Postlam</SelectItem>
                            <SelectItem value="gold">Gold — Commercial Ply</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="text-xs text-slate-500 flex-1">Applies to all products by default. Override on individual cards.</div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    <button
                        onClick={() => { setActiveCat(null); setActiveSub(null); }}
                        data-testid="filter-all"
                        className={`px-4 py-2 text-sm font-medium border transition-colors ${!activeCat && !activeSub ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-900'}`}
                    >
                        All ({products.length})
                    </button>
                    {CATEGORIES.map((cat) => {
                        const count = products.filter((p) => p.category === cat).length;
                        const active = activeCat === cat;
                        return (
                            <button
                                key={cat}
                                onClick={() => { setActiveCat(cat); setActiveSub(null); }}
                                data-testid={`filter-${cat.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                                className={`px-4 py-2 text-sm font-medium border transition-colors ${active ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-900'}`}
                            >
                                {cat} <span className="opacity-60">({count})</span>
                            </button>
                        );
                    })}
                </div>

                {/* Sub-filters */}
                {activeCat && availableSubs.length > 0 && (
                    <div className="mb-6 border-l-2 border-amber-500 pl-4" data-testid="sub-filters">
                        <div className="eyebrow mb-2 text-amber-700">Refine by type</div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setActiveSub(null)}
                                data-testid="subfilter-all"
                                className={`px-3 py-1.5 text-xs font-medium border transition-colors ${!activeSub ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-600'}`}
                            >
                                All types
                            </button>
                            {availableSubs.map((sub) => (
                                <button
                                    key={sub.label}
                                    onClick={() => setActiveSub(sub.label)}
                                    data-testid={`subfilter-${sub.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                                    className={`px-3 py-1.5 text-xs font-medium border transition-colors ${activeSub === sub.label ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-600'}`}
                                >
                                    {sub.label} <span className="opacity-60">({sub.count})</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {(activeCat || activeSub) && (
                    <div className="flex items-center gap-3 mb-4 text-sm text-slate-600">
                        Showing <strong className="text-slate-900">{[activeCat, activeSub].filter(Boolean).join(' · ')}</strong>
                        <button onClick={() => { setActiveCat(null); setActiveSub(null); }} className="inline-flex items-center gap-1 text-blue-900 hover:underline"><X className="w-3 h-3" /> Clear</button>
                    </div>
                )}

                <div className="text-xs text-slate-500 mb-4">Showing {filtered.length} products.</div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="modules-grid">
                    {filtered.map((p) => <ProductCard key={p.id} product={p} onOpen={setModalProduct} />)}
                </div>

                {filtered.length === 0 && (
                    <div className="text-center py-16 text-slate-500">No products match this filter.</div>
                )}
            </section>

            <ProductDetailModal product={modalProduct} open={!!modalProduct} onOpenChange={(v) => !v && setModalProduct(null)} />
        </div>
    );
}
